import Constants from '@/Constants';
import { AvailableLanguage } from '@/models/AvailableLanguage';
import { LanguageType } from '@/models/LanguageType';
import { ApplicationState } from '@/store';
import { closeModal, openModal } from '@/store/common/actions';
import { setCurrentContentLanguage } from '@/store/locale/action';
import React, { useState } from 'react';
import Modal from 'react-bootstrap/esm/Modal';
import { useDispatch, useSelector } from 'react-redux';
import Menu from '../ContentMoreOptions/Menu';
import Loader from '../Loader';
import './style.scss';

interface Language {
    locale: LanguageType;
    label: string;
}

const ChangeContentLanguageModal: React.FC = () => {
    const { content, currentContentLanguage, loading, strings } = useSelector((state: ApplicationState) => ({
        content: state.itens.currentData.content,
        currentContentLanguage: state.locale.currentContentLanguage || (state.locale.data._LANGUAGE as LanguageType),
        loading: state.itens.loading,
        strings: state.locale.data,
    }));

    const [activeLanguage, setActiveLanguage] = useState(currentContentLanguage);

    const dispatch = useDispatch();

    const languages = [
        { locale: Constants.language.EN, label: strings.ENGLISH },
        { locale: Constants.language.PT, label: strings.PORTUGUESE },
    ] as Language[];

    const changeLanguage = (languageType: LanguageType) => {
        setActiveLanguage(languageType);
    };

    const getLanguageDescription = (languageType: LanguageType) => {
        const currentLanguage = languages.find((language: Language) => language.locale === languageType);

        if (currentLanguage) {
            const description = currentLanguage.label;

            if (isDefaultLanguage(languageType)) {
                return `${description} (${strings.ORIGINAL_LANGUAGE})`;
            }
            return description;
        }
        return '';
    };

    const isDefaultLanguage = (languageType: LanguageType): boolean => {
        return content.defaultLocale === languageType;
    };

    const dismissModalHandler = () => {
        dispatch(closeModal(Constants.modal.MORE_OPTIONS));
    };

    const handleSubmit = () => {
        dispatch(setCurrentContentLanguage(activeLanguage));
        dismissModalHandler();
    };

    const handleGoBack = () => {
        dispatch(openModal({ id: Constants.modal.MORE_OPTIONS, layoutModal: <Menu />, isVisible: true, isCloseAllowed: true }));
    };

    return (
        <>
            {loading && <Loader />}
            {!loading && content && content.availableLanguages && (
                <div className="change-language-content">
                    <Modal.Header>
                        <div className="header-info">
                            <i className="material-icons-outlined align-middle" onClick={handleGoBack}>
                                {Constants.icons.ARROW_LEFT}
                            </i>
                            <Modal.Title>
                                <span className="title">{strings.AVAILABLE_LANGUAGES}</span>
                            </Modal.Title>
                        </div>
                        <div className="brand-gradient"></div>
                    </Modal.Header>
                    <Modal.Body>
                        {content.availableLanguages.map((availableLanguage: AvailableLanguage) => {
                            return (
                                <div
                                    className="available-languages"
                                    key={availableLanguage.locale}
                                    onClick={() => changeLanguage(availableLanguage.locale)}
                                >
                                    <span className={activeLanguage === availableLanguage.locale ? 'active' : ''}>
                                        {getLanguageDescription(availableLanguage.locale)}
                                    </span>
                                    {activeLanguage === availableLanguage.locale && (
                                        <i className="material-icons-outlined align-middle">{Constants.icons.DONE}</i>
                                    )}
                                </div>
                            );
                        })}
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            className="btn btn-primary btn-block"
                            data-dismiss="modal"
                            disabled={currentContentLanguage === activeLanguage}
                            onClick={handleSubmit}
                            type="button"
                        >
                            {strings.CONCLUDE}
                        </button>
                    </Modal.Footer>
                </div>
            )}
        </>
    );
};

export default ChangeContentLanguageModal;
